import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showError, showSuccess } from "toast/toast";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import {
  addressLine1Validation,
  cityValidation,
  emailValidation,
  firstNameValidation,
  lastNameValidation,
  passwordValidation,
  phoneValidation,
  stateValidation,
  statusValidation,
  zipcodeValidation,
} from "validationSchemas/common";
import AgentForm, { AgentFormValues, SelectOption } from "components/AgentsAddEditComponent";
import { uploadCloudFile, getCloudFileById, resolveImageId } from "components/Imageshelper";
import { getAuthCookie } from "../../utils/authCookies";

// Select2 type for brokerage firms
type BrokerageOption = {
  value: string;
  label: string;
  id: string;
  firmDetails: { companyName: string };
};

type Office = {
  _id: string;
  officeName: string;
  officeType: string;
  officeAddress: Array<{
    _id: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }>;
};

type AgentAPI = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone1: string;
  phone2?: string;
  status: string;
  profilePic?: string | null;
  created_by?: { id?: string; name?: string };
  firmDetails?: {
    address?: Partial<{
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      country: string;
      zip: string;
      cityCode: string;
      stateCode: string;
      countryCode: string;
      latitude: number;
      longitude: number;
    }>;
  };
  agentDetails?: Partial<{
    brokerageFirmAffiliation: string; // a name on your API
    agentID: string;
    licenseNumber: string;
    licenseExpiryDate: string;
  }>;
  offices?: Array<{
    _id: string;
    officeName: string;
    officeType: string;
  }>;
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];
const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

const toUrlString = (res: unknown): string | undefined => {
  if (!res) return undefined;
  if (typeof res === "string") return res;
  const r = res as any;
  const u = r?.url ?? r?.signedUrl ?? r?.data?.url;
  return typeof u === "string" ? u : undefined;
};


export default function SignageAddEditBrokerageAgent() {
  const { firmId } = useParams<{ firmId: string }>();
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = Boolean(agentId);
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAuthCookie("sessionToken") || "";

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  // data sources (always arrays)
  const [offices, setOffices] = useState<Office[]>([]);
  const [agent, setAgent] = useState<AgentAPI | null>(null);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [serverImageSrc, setServerImageSrc] = useState<string | undefined>(undefined);
  const [cloudFileId, setCloudFileId] = useState<string | null>(null);
  const userId = getAuthCookie("userId") || "";
  
  // State for parent signage firm (for signageteammember login)
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);
  
  // State for AsyncPaginate selected options
  const [selectedBrokerageOption, setSelectedBrokerageOption] = useState<BrokerageOption | null>(null);
  
  // Fetch parent hierarchy for signageteammember login
  const fetchParentHierarchy = async () => {
    if (!isSignageTeammember || !token) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Find the parent with license_type "SIGNAGE_FIRM"
        const signageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "SIGNAGE_FIRM"
        );
        
        if (signageFirm) {
          setParentSignageId(signageFirm.id || signageFirm._id);
          setParentSignageName(signageFirm.name);
        }
      }
    } catch (error) {
      console.error("Error fetching parent hierarchy:", error);
    }
  };

  // Helper function and constants for AsyncPaginate
  const normalizeId = (row: any) => String(row?.value ?? row?.id ?? row?._id ?? "").trim();
  const PAGE_SIZE = 20;
  
  // ===== Brokerage Firm Loader =====
  const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const ownerId = (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "";

      if (!ownerId) {
        return { options: [], hasMore: false, additional: { page } };
      }

      try {
        const q = (inputValue || "").trim();

        const params = new URLSearchParams({
          licenseType: "BROKERAGE_FIRM",
          query: q,
          page: String(page),
          limit: String(PAGE_SIZE),
        });

        const url = `${process.env.REACT_APP_API_URL}/customer-groups/${ownerId}/users-created-only?${params}`;

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch brokerage firms:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();
        const list = Array.isArray(json?.data) ? json.data : [];

        const options: BrokerageOption[] = list.map((u: any) => {
          const companyName =
            u?.firmDetails?.companyName ||
            u?.company_name ||
            u?.label ||
            u?.text ||
            "(no name)";
          return {
            value: normalizeId(u),
            label: companyName,
            id: normalizeId(u),
            firmDetails: { companyName },
          };
        });

        const currentPage = Number(json?.page ?? page);
        const totalPages = Number(json?.totalPages ?? currentPage);
        const hasMore = currentPage < totalPages && list.length >= PAGE_SIZE;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        console.error("Failed to load brokerage options:", err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isSignageTeammember, parentSignageId, token]
  );

  // Fetch parent hierarchy for signageteammember login
  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isSignageTeammember, token]);
  
 const firmIdFromRoute = (firmId || "").trim();
  const normalizePath = (p: string) => p.replace(/\\/g, "/").replace(/^\/+/, "");
  const buildAbsoluteFromRelative = (p: string) => {
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    return `${base}/${normalizePath(p)}`;
  };
  useEffect(() => {
    let active = true;

    (async () => {
      const val = agent?.profilePic;
      if (!val) {
        if (active) setServerImageSrc(undefined);
        return;
      }

      if (/^https?:\/\//i.test(val)) {
        if (active) setServerImageSrc(val);
        return;
      }

      if (/^[0-9a-f]{24}$/i.test(val)) {
        setCloudFileId(val);
        try {
          const res = await getCloudFileById(val, token as string);
          console.log(res, "tetetetete")
          const u = toUrlString(res);
          if (active) setServerImageSrc(u);
        } catch {
          if (active) setServerImageSrc(undefined);
        }
        return;
      }

      if (active) setServerImageSrc(buildAbsoluteFromRelative(val));
    })();

    return () => {
      active = false;
    };
  }, [agent?.profilePic, token]); // ⬅️ removed serverImageSrc from deps

  // ---------- fetch firm by firmId from URL ----------
  useEffect(() => {
    if (!firmIdFromRoute) return;
    
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${firmIdFromRoute}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const firmData = await res.json();
        if (!res.ok || firmData?.error) throw new Error(firmData?.error || "Failed to load firm");
        
        // Create BrokerageOption from the fetched firm data
        const brokerageOption: BrokerageOption = {
          value: normalizeId(firmData),
          label: firmData?.firmDetails?.companyName || firmData?.firstName + " " + firmData?.lastName || "(no name)",
          id: normalizeId(firmData),
          firmDetails: { 
            companyName: firmData?.firmDetails?.companyName || firmData?.firstName + " " + firmData?.lastName || "(no name)" 
          },
        };
        
        setSelectedBrokerageOption(brokerageOption);
        console.log("brokerageOption",brokerageOption)
        // Also set the form field value
        formik.setFieldValue("brokerageFirmAffiliation", brokerageOption.value, false);
        
      } catch (e: any) {
        showError(e.message || "Failed to load firm");
      }
    })();
  }, [firmIdFromRoute, token]);

  // ---------- fetch agent (edit) ----------
  useEffect(() => {
    if (!isEdit) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${agentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = (await res.json()) as AgentAPI;
        if (!res.ok) throw new Error("Failed to load agent");
        setAgent(data);
      } catch (e: any) {
        showError(e.message || "Failed to load agent");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, agentId, token]);
  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;
  const toCoordinate = (value: unknown) => {
    const coordinate = Number(value ?? 0);
    return Number.isFinite(coordinate) ? coordinate : 0;
  };

  // ---------- initial values ----------
  const initialValues: AgentFormValues = useMemo(
    () => ({
      // personal
      firstName: agent?.firstName || "",
      lastName: agent?.lastName || "",
      phone1: agent?.phone1 || DEFAULT_TEN_ZEROS,
      phone2: agent?.phone2 || "",
      // account
      email: agent?.email || "",
      password: "",
      // professional
      brokerageFirmAffiliation: "", // FIRM ID will be stored here
      officeId: "",
      agentID: agent?.agentDetails?.agentID || "",
      licenseNumber: agent?.agentDetails?.licenseNumber || "",
      licenseExpiryDate: agent?.agentDetails?.licenseExpiryDate || "",
      statuss: agent?.status || "Active",
      // address
      addressLine1: agent?.firmDetails?.address?.addressLine1 || "",
      addressLine2: agent?.firmDetails?.address?.addressLine2 || "",
      city: agent?.firmDetails?.address?.city || "",
      state: agent?.firmDetails?.address?.state || "",
      country: agent?.firmDetails?.address?.country || US_COUNTRY_DEFAULTS.country,
      zipcode: agent?.firmDetails?.address?.zip || "",
      cityCode: agent?.firmDetails?.address?.cityCode || "",
      stateCode: agent?.firmDetails?.address?.stateCode || "",
      countryCode: agent?.firmDetails?.address?.countryCode || US_COUNTRY_DEFAULTS.countryCode,
      latitude: toCoordinate(agent?.firmDetails?.address?.latitude),
      longitude: toCoordinate(agent?.firmDetails?.address?.longitude),
      // profile
      image: null,
      profilePic: agent?.profilePic || null,
      displaySrc: agent?.profilePic || null,
    }),
    [agent]
  );

  // ---------- formik (parent only) ----------
  const formik = useFormik<AgentFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      phone1: phoneValidation,
      email: emailValidation,
      // password: isEdit ? Yup.string() : passwordValidation,
      // addressLine1: addressLine1Validation,
      // city: cityValidation,
      // state: stateValidation,
      // zipcode: zipcodeValidation,
      // statuss: statusValidation,
    }),
    onSubmit: async (values) => {
      try {
        setFormSubmitting(true);
        let imageFileId = cloudFileId || ""; // keep existing if present
        if (values.image instanceof File) {
          const uploaded = await uploadCloudFile({ file: values.image, token });
          imageFileId = uploaded.id;
        }
        const generated_id = await resolveImageId(values.image, token, imageFileId);
        // resolve firm from selected brokerage option
        const firm = selectedBrokerageOption;
        console.log("firm",firm)
        // resolve office by ID
        const office = offices.find((o) => o._id === values.officeId)
        const addressCountry = values.country?.trim() || US_COUNTRY_DEFAULTS.country;
        const addressCountryCode = values.countryCode?.trim() || US_COUNTRY_DEFAULTS.countryCode;
        const addressLatitude = toCoordinate(values.latitude);
        const addressLongitude = toCoordinate(values.longitude);

        const payload = {
          email: values.email,
          password: values.password,
          userType: "agent",
          firstName: values.firstName,
          lastName: values.lastName,
          phone1: withDefault(values.phone1) || values.phone1,
          phone2: values.phone2 || "",
          licenseType: "BROKERAGE_AGENT",
          status: values.statuss,
          profilePic: generated_id || undefined,
          firmDetails: {
            companyID: "",
            companyName: "",
            companyWebsite: "",
            address: {
              addressLine1: values.addressLine1,
              addressLine2: values.addressLine2,
              city: values.city,
              state: values.state,
              country: addressCountry,
              zip: values.zipcode,
              cityCode: values.cityCode,
              stateCode: values.stateCode,
              countryCode: addressCountryCode,
              latitude: addressLatitude,
              longitude: addressLongitude,
              phone: withDefault(values.phone1) || values.phone1,
            },
          },
          agentDetails: {
            brokerageFirmAffiliation: firm?.firmDetails?.companyName,
            brokerageFirmName: firm?.firmDetails?.companyName,
            brokerageFirmID: firm?.value,
            agentID: values.agentID,
            licenseNumber: values.licenseNumber,
            licenseExpiryDate: values.licenseExpiryDate,
            licenseActive: false,
          },
          offices: office
            ? [
              {
                _id: office._id,
                officeName: office.officeName,
                officeType: office.officeType,
                officeAddress:
                  office.officeAddress?.[0] && {
                    _id: office.officeAddress[0]._id,
                    addressLine1: office.officeAddress[0].addressLine1,
                    city: office.officeAddress[0].city,
                    state: office.officeAddress[0].state,
                    country: office.officeAddress[0].country,
                    zip: office.officeAddress[0].zip,
                  },
              },
            ]
            : [],
          created_by: firm ? { id: firm.value, name: firm.firmDetails.companyName } : undefined,
        };

        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/users/${agentId}`
          : `${process.env.REACT_APP_API_URL}/user/create`;
        const method = isEdit ? "PATCH" : "POST";

        const res = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const result = await res.json();
        if (!res.ok || result?.error) throw new Error(result?.error || "Failed to save agent");

        const hasAddressDetails = [
          values.addressLine1,
          values.city,
          values.state,
          values.zipcode,
        ].some((field) => field?.trim());

        // EXTRA: create address only on ADD
        if (!isEdit && hasAddressDetails) {
          try {
            const addrRes = await fetch(
              `${process.env.REACT_APP_API_URL}/addresses/user/${result.id}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  user_id: String(result.id),
                  label: `${values.firstName} ${values.lastName || ""}`,
                  first_name: values.firstName,
                  last_name: values.lastName,
                  company: "",
                  address_details: {
                    label: `${values.firstName} ${values.lastName || ""}`,
                    addressLine1: values.addressLine1,
                    addressLine2: values.addressLine2,
                    city: values.city,
                    state: values.state,
                    zip: values.zipcode,
                    country: addressCountry,
                    phone: withDefault(values.phone1) || values.phone1,
                    cityCode: values.cityCode,
                    stateCode: values.stateCode,
                    countryCode: addressCountryCode,
                    latitude: addressLatitude,
                    longitude: addressLongitude,
                  },
                }),
              }
            );
            const addrJson = await addrRes.json();
            if (!addrRes.ok || addrJson?.error) {
              showError(addrJson?.error || "Failed to create address");
            }
          } catch { }
        }

        showSuccess(isEdit ? "Agent updated successfully!" : "Agent added successfully!");
        // navigate("/signage/agents");
        navigate(-1);
      } catch (e: any) {
        showError(e.message || "Failed to save agent");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  // This useEffect is now handled by the fetch firm by firmId useEffect above

console.log(firmId,"firmids")

  // fetch offices when firm changes
  useEffect(() => {
    const firmId = formik.values.brokerageFirmAffiliation;
    if (!firmId) {
      setOffices([]);
      return;
    }

    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/offices-search?created_by.id=${firmId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        console.log(data,"dadadadada")
        if (!res.ok || data?.error) throw new Error(data?.error || "Failed to fetch offices");
        setOffices(Array.isArray(data) ? (data as Office[]) : []);
      } catch (e: any) {
        showError(e.message || "Failed to fetch offices");
        setOffices([]);
      }
    })();
  }, [formik.values.brokerageFirmAffiliation, token,firmId]);

  // ---------- HYDRATE firm/office on EDIT ----------
  // 1) map agent firm name or created_by.id to firm ID → set in Formik and selectedBrokerageOption
  useEffect(() => {
    if (!isEdit || !agent) return;

    // try created_by.id first
    let firmId = agent.created_by?.id || "";

    // fallback by name (agentDetails.brokerageFirmAffiliation)
    if (!firmId) {
      const wantName = (agent.agentDetails?.brokerageFirmAffiliation || "")
        .trim();
      // Create a BrokerageOption object for the selected firm
      const brokerageOption: BrokerageOption = {
        value: firmId,
        label: wantName,
        id: firmId,
        firmDetails: { companyName: wantName }
      };
      setSelectedBrokerageOption(brokerageOption);
    } else {
      // Create a BrokerageOption object for the selected firm
      const firmName = agent.agentDetails?.brokerageFirmAffiliation || agent.created_by?.name || "";
      const brokerageOption: BrokerageOption = {
        value: firmId,
        label: firmName,
        id: firmId,
        firmDetails: { companyName: firmName }
      };
      setSelectedBrokerageOption(brokerageOption);
    }

    if (firmId && formik.values.brokerageFirmAffiliation !== firmId) {
      formik.setFieldValue("brokerageFirmAffiliation", firmId, false);
    }
  }, [isEdit, agent]); // removed firms dependency

  // 2) after offices arrive, set agent's officeId
  useEffect(() => {
    if (!isEdit || !agent || !offices.length) return;
    const agentOfficeId = agent.offices?.[0]?._id || "";
    if (agentOfficeId && formik.values.officeId !== agentOfficeId) {
      formik.setFieldValue("officeId", agentOfficeId, false);
    }
  }, [isEdit, agent, offices]); // keep deps

  if (loading) return <div className="content">Loading...</div>;

  // options for child (always arrays) - now using AsyncPaginate
  console.log(selectedBrokerageOption,"selectedBrokerageOption")
  const brokerageFirmOptions: SelectOption[] = selectedBrokerageOption ? [{
    value: selectedBrokerageOption.value,
    label: selectedBrokerageOption.firmDetails.companyName,
  }] : [];

  const officeOptions: SelectOption[] = (offices ?? []).map((o) => ({
    value: o._id,
    label: o.officeName,
  }));
  console.log(officeOptions,"officeOptionsmain")
  const joinUrl = (base?: string, path?: string) => {
    if (!path) return "";
    if (!base) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const b = base.replace(/\/+$/, "");
    const p = path.replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const displaySrc: string | undefined =
    serverImageSrc ??
    (typeof formik.values.profilePic === "string" && /^\/?uploads\//i.test(formik.values.profilePic)
      ? joinUrl(process.env.REACT_APP_BASE_URL, formik.values.profilePic)
      : undefined);
console.log(brokerageFirmOptions,"brokerageFirmOptionsmainparent")

  return (
    <AgentForm
      isEdit={isEdit}
      formik={formik}
      brokerageFirmOptions={brokerageFirmOptions}
      officeOptions={officeOptions}
      statusOptions={STATUS_OPTIONS}
      displaySrc={displaySrc}
      onCancel={() => navigate(-1)}
      onSubmit={() => formik.handleSubmit()}
      submitting={formSubmitting}
      type={"Brokerage_Agent"}
      enableGoogleAddressDetailsFlow
      // AsyncPaginate props
      loadBrokerageOptions={loadBrokerageOptions}
      selectedBrokerageOption={selectedBrokerageOption}
      onBrokerageChange={setSelectedBrokerageOption}
    />
  );
}
